import type { FormEvent } from 'react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  getSunoPrompt,
  suggestTrackTitles,
  type TrackTitleLanguage,
  type TrackTitleSuggestion
} from '../../../api/suno'

export const languageOptions: Array<{ label: string; value: TrackTitleLanguage }> = [
  { label: 'English', value: 'en' },
  { label: 'Auto', value: 'auto' },
  { label: 'Russian', value: 'ru' }
]

export const useTrackTitleGeneratorData = () => {
  const [searchParams] = useSearchParams()
  const namePromptId = searchParams.get('namePromptId')

  const [stylePrompt, setStylePrompt] = useState('')
  const [artist, setArtist] = useState('')
  const [language, setLanguage] = useState<TrackTitleLanguage>('en')
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<TrackTitleSuggestion[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const promptValid = stylePrompt.trim().length > 0
  const selectedLanguageLabel = languageOptions.find((option) => option.value === language)?.label ?? 'English'

  useEffect(() => {
    if (!namePromptId) {
      return
    }

    let ignore = false

    const loadNamePrompt = async (): Promise<void> => {
      try {
        const prompt = await getSunoPrompt(namePromptId)

        if (!ignore) {
          setStylePrompt(prompt.style_prompt)
          setError(null)
        }
      } catch (requestError) {
        if (!ignore) {
          setError(requestError instanceof Error ? requestError.message : 'Unable to load selected prompt.')
        }
      }
    }

    void loadNamePrompt()

    return () => {
      ignore = true
    }
  }, [namePromptId])

  const submitTrackTitles = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()

    const trimmedPrompt = stylePrompt.trim()

    if (!trimmedPrompt) {
      setError('Style prompt is required.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const result = await suggestTrackTitles({
        artist: artist.trim() || null,
        language,
        style_prompt: trimmedPrompt
      })

      setSuggestions(result.items)
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to generate track names.')
    } finally {
      setLoading(false)
    }
  }

  const selectLanguage = (nextLanguage: TrackTitleLanguage): void => {
    setLanguage(nextLanguage)
    setLanguageMenuOpen(false)
  }

  return {
    artist,
    setArtist,
    stylePrompt,
    setStylePrompt,
    language,
    languageMenuOpen,
    setLanguageMenuOpen,
    suggestions,
    error,
    loading,
    promptValid,
    selectedLanguageLabel,
    selectLanguage,
    submitTrackTitles
  }
}
